import SlideController from './media_slider/slide_controller'
import eventBus from '../event_bus'
import { hasTruthyAttribute } from '../helpers/attributes'

const SWIPE_PX_THRESHOLD = 50
const SWIPE_MS_THRESHOLD = 500
const SWIPE_ANIMATION_THRESHOLD = 20

function isSwipeLeft (pxDiff, msDiff) {
  return msDiff < SWIPE_MS_THRESHOLD && pxDiff > SWIPE_PX_THRESHOLD
}

function isSwipeRight (pxDiff, msDiff) {
  return msDiff < SWIPE_MS_THRESHOLD && pxDiff < -SWIPE_PX_THRESHOLD
}

class CarwowMediaSlider extends HTMLElement {
  static get observedAttributes () { return ['items', 'link_to'] }

  get nextSlideTrigger () {
    return this.querySelector('[data-role="media-slider__next"]')
  }

  get previousSlideTrigger () {
    return this.querySelector('[data-role="media-slider__prev"]')
  }

  get currentSlideNumber () {
    const currentSlide = this.controller.currentSlide

    return currentSlide ? currentSlide.number : 1
  }

  connectedCallback () {
    this.isInGalleryOverlay = hasTruthyAttribute(this, 'is_in_gallery_overlay')
    this.lazyLoadFirstImages = hasTruthyAttribute(this, 'lazy_load_first_images')
    this.connected = true

    // defer slider initialisation when it's in the gallery
    if (!this.isInGalleryOverlay) {
      this.render()
    }

    this.listenToGalleryEvents()
  }

  attributeChangedCallback (name) {
    if (name !== 'items' && name !== 'link_to') return

    this.render()
  }

  render () {
    if (!this.connected) return
    const data = this.sanitizeItems(JSON.parse(this.getAttribute('items')))

    if (data.length === 0) { this.handleNoSlides(); return }

    this.setup(data)
    this.buildDOMStructure()

    if (data.length === 1) { return }

    this.resetPosition()
    this.updateCurrentSlideIndicator()

    this.addSwipeGestures()
    this.addClickCallback()
    this.addTransitionCallback()
  }

  setup (data) {
    this.id = this.hasAttribute('id') ? this.getAttribute('id') : ''

    const options = {
      lazyLoadFirstImages: hasTruthyAttribute(this, 'lazy_load_first_images'),
      linkTo: this.getAttribute('link_to'),
      linkToTarget: this.getAttribute('link_to_target'),
      linkToTracking: this.getAttribute('link_to_tracking'),
      cropImages: this.getAttribute('crop_images') !== 'false',
      enableZoomControls: hasTruthyAttribute(this, 'enable_zoom_controls')
    }

    if (options.linkTo && options.enableZoomControls) {
      throw new Error('The following flags are not compatible: "linkTo" and "enableZoomControls"')
    }

    this.controller = new SlideController(
      data,
      options,
      0,
      () => {
        // remove existing slides from DOM
        this.row.textContent = ''

        this.assignSlides()
        this.updateCurrentSlideIndicator()
      }
    )
  }

  handleNoSlides () {
    this.classList.add('media-slider--empty')
  }

  clearDOM () {
    this.classList.remove('media-slider--empty')
    this.querySelectorAll('.media-slider__pages').forEach(e => e.remove())
    this.querySelectorAll('.media-slider__frame').forEach(e => e.remove())
  }

  buildDOMStructure () {
    this.clearDOM()

    this.frame = document.createElement('div')
    this.frame.className = 'media-slider__frame'
    this.appendChild(this.frame)

    this.row = document.createElement('div')
    this.row.className = 'media-slider__images'
    this.frame.appendChild(this.row)
    this.nav = this.querySelector('[data-role="media-slider__nav"]')

    if (this.controller.numberOfSlides > 1) {
      this.assignSlides()
      this.nav.classList.remove('media-slider__nav--hidden')
      this.currentSlideIndicator = document.createElement('div')
      this.currentSlideIndicator.className = 'media-slider__pages'
      this.nav.appendChild(this.currentSlideIndicator)
    } else {
      const slide = this.controller.slides[0]
      slide.build(this.row)
    }
  }

  assignSlides () {
    // this check is needed for when there are more than one item passed into component
    // but slides cannot be created from them because of broken image urls
    if (this.controller.numberOfSlides === 0) { this.handleNoSlides(); return }

    this.controller.slides.forEach((slide) => slide.hide())

    const numberOfSlides = 3
    const activeSlidesIndexes = this.controller.activeSlidesIndexes();

    [...Array(numberOfSlides)].forEach((el, ind) => {
      const slide = this.controller.slides[activeSlidesIndexes[ind]]

      if (!slide.isBuilt) {
        slide.build(this.row)
      }

      slide.assignPosition(ind)
    })
  }

  addSwipeGestures () {
    this.addEventListener(
      'touchstart',
      this.touchStart.bind(this),
      { passive: true }
    )

    this.addEventListener(
      'touchend',
      this.touchStop.bind(this),
      false
    )

    this.addEventListener(
      'touchmove',
      this.touchMove.bind(this),
      { passive: false }
    )
  }

  addClickCallback () {
    this.addEventListener(
      'click',
      this.handleClick.bind(this),
      false
    )
  }

  addTransitionCallback () {
    this.addEventListener(
      'transitionend',
      this.handleTransitionEnd.bind(this),
      false
    )
  }

  touchStart (e) {
    if (this.transitioning) return

    this.touchstartX = e.changedTouches[0].screenX
    this.touchstartY = e.changedTouches[0].screenY
    this.touchstartTime = new Date()
  }

  touchMove (e) {
    if (this.transitioning) return

    const frameWidth = this.frame.offsetWidth

    const diffX = e.changedTouches[0].screenX - this.touchstartX
    const diffY = e.changedTouches[0].screenY - this.touchstartY

    // Prevent scroll if needed
    if (Math.abs(diffX) > Math.abs(diffY)) {
      e.preventDefault()
    }

    if (!this.slowSwipeStarted && Math.abs(diffX) < SWIPE_ANIMATION_THRESHOLD) return
    this.slowSwipeStarted = true

    const offset = Math.max(
      -frameWidth,
      Math.min(diffX, frameWidth)
    )

    this.row.style.transform = `translateX(${-frameWidth + offset}px)`
  }

  touchStop (e) {
    this.slowSwipeStarted = false
    if (this.transitioning) return

    const pxDiff = e.changedTouches[0].screenX - this.touchstartX
    const msDiff = new Date() - this.touchstartTime

    if (isSwipeLeft(pxDiff, msDiff)) {
      this.changeSlide(-1)
    } else if (isSwipeRight(pxDiff, msDiff)) {
      this.changeSlide(1)
    } else {
      this.closestSlide(pxDiff)
    }
  }

  handleClick (e) {
    if (e.target === this.nextSlideTrigger) {
      e.preventDefault()
      e.stopPropagation() // Do not trigger "clicked link" event in amplitude

      if (this.transitioning) return

      this.changeSlide(1)
    } else if (e.target === this.previousSlideTrigger) {
      e.preventDefault()
      e.stopPropagation() // Do not trigger "clicked link" event in amplitude
      if (this.transitioning) return

      this.changeSlide(-1)
    }
  }

  runTransition () {
    this.transitioning = true
    this.classList.add('media-slider--transitioning')
  }

  handleTransitionEnd (e) {
    if (e.target === this.row) {
      this.classList.remove('media-slider--transitioning')
      this.transitioning = false

      this.assignSlides()
      this.resetPosition()
    }
  }

  closestSlide (pxDiff) {
    const frameWidth = this.frame.offsetWidth

    if (pxDiff > frameWidth / 2) {
      this.changeSlide(-1)
    } else if (pxDiff < -frameWidth / 2) {
      this.changeSlide(1)
    } else {
      if (Math.abs(pxDiff) > SWIPE_ANIMATION_THRESHOLD) this.runTransition()
      this.resetPosition()
    }
  }

  resetPosition () {
    this.row.style.transform = 'translateX(-100%)'
  }

  changeSlide (offset) {
    if (offset === 1) {
      this.controller.nextSlide()
    } else {
      this.controller.prevSlide()
    }

    this.runTransition()
    this.dispatchSlideChangeEvent()
    this.updateCurrentSlideIndicator()

    this.row.style.transform = `translateX(-${(1 + offset) * 100}%)`
  }

  jumpToSlide (slideIndex) {
    this.controller.jumpToSlide(slideIndex)

    this.dispatchSlideChangeEvent()
    this.updateCurrentSlideIndicator()
    this.assignSlides()
    this.resetPosition()
  }

  updateCurrentSlideIndicator () {
    if (this.controller.numberOfSlides > 1) {
      this.currentSlideIndicator.innerHTML = `${(this.currentSlideNumber)}/${this.controller.numberOfSlides}`
    }
  }

  sanitizeItems (items) {
    return items.filter(item => item.img)
  }

  // listen to thumbnail clicks and close button click in gallery overlay
  listenToGalleryEvents () {
    eventBus.on('open-gallery-cta-click', (e) => {
      if (e.targetSlider !== this.id) return

      // initialise the slider since it is visible now
      this.render()

      const slideStartingIndex = e.slideStartingIndex
      this.jumpToSlide(slideStartingIndex)
    })

    eventBus.on('gallery-thumbnail-click', (e) => {
      console.log('On Gallery Thumbnail click')
      if (e.targetSlider !== this.id) return

      const selectedThumbnailIndex = e.thumbnailIndex
      const offset = selectedThumbnailIndex - this.controller.currentSlideIndex

      if (Math.abs(offset) > 1) {
        this.jumpToSlide(selectedThumbnailIndex) // jumps over more than one slide
      } else {
        this.changeSlide(offset) // moves by one slide
      }
    })

    eventBus.on('close-gallery-click', (e) => {
      if (e.targetSlider !== this.id) return

      if (this.controller.currentSlide.playerInitiated) {
        this.controller.stop()
      }
    })
  }

  dispatchSlideChangeEvent () {
    eventBus.emit('slider-slide-change', {
      galleryId: this.id,
      inGallery: this.isInGalleryOverlay,
      currentSlideIndex: this.currentSlideNumber - 1
    })
  }
}

window.customElements.define('carwow-media-slider', CarwowMediaSlider)
